import { useSyncExternalStore } from "react";
import { useSession } from "./session";
import type { ApprovalsDTO, MemberDTO } from "@shared/types";

/**
 * Which kid the parent app is focused on ("All kids" when null). Remembered per device, so a
 * parent who mostly handles one kid on this phone lands back on that kid after a reload.
 */
const KEY = "mr.parent.kid";
const listeners = new Set<() => void>();
let memory: string | null = null;

function read(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return memory;
  }
}

export function setSelectedKid(kidId: string | null) {
  memory = kidId;
  try {
    if (kidId) localStorage.setItem(KEY, kidId);
    else localStorage.removeItem(KEY);
  } catch {
    // Private mode or blocked storage: the in-memory value still works for this visit.
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => e.key === KEY && l();
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener("storage", onStorage);
  };
}

/** The selected kid, or null for the whole family (also when the saved kid is no longer in it). */
export function useSelectedKid(): MemberDTO | null {
  const id = useSyncExternalStore(subscribe, read, () => null);
  const { kids } = useSession();
  return kids.find((k) => k.id === id) ?? null;
}

/** Just one kid's part of the approvals queue (pitches count for the kid who pitched them). */
export function approvalsForKid(a: ApprovalsDTO, kidId: string): ApprovalsDTO {
  const jobRuns = a.jobRuns.filter((r) => r.kidId === kidId);
  const pitches = a.pitches.filter((j) => j.pitchedBy === kidId);
  const redemptions = a.redemptions.filter((r) => r.kidId === kidId);
  const completions = a.completions.filter((c) => c.kidId === kidId);
  const toDeliver = a.toDeliver.filter((r) => r.kidId === kidId);
  // Suggested tasks for this kid, or for every kid.
  const suggestions = a.suggestions.filter((t) => !t.kidIds.length || t.kidIds.includes(kidId));
  return {
    jobRuns,
    pitches,
    suggestions,
    redemptions,
    completions,
    toDeliver,
    count: jobRuns.length + pitches.length + suggestions.length + redemptions.length + completions.length,
  };
}
